// ============================================================================
// Shared Milestone Catalog (single source of truth)
// ----------------------------------------------------------------------------
// Used by both the main app (PFMilestones.js / PositionFoldersPage.js) and the
// Outlook Add-in (OutlookAddinPage.js) so the dropdown options, group ordering,
// and human-readable labels stay in lock-step. If you add a new milestone here,
// it shows up everywhere automatically — no need to touch the plugin or any
// other consumer. Backend label mapping lives in
// /app/backend/services/milestone_catalog.py (mirror).
// ============================================================================
import {
  Circle,
  Ship,
  Anchor,
  MapPin,
  FileText,
  Truck,
  Building2,
  ClipboardCheck,
  ShieldCheck,
  Globe,
  Warehouse,
  CheckSquare,
} from "lucide-react";

export const MILESTONE_TEMPLATES = {
  "Order & Pre-Transport": {
    icon: FileText,
    color: "text-slate-600",
    items: [
      { value: "order_received", label: "Order received" },
      { value: "transport_order_created", label: "Transport order created" },
      { value: "booking_requested", label: "Booking requested with carrier" },
      { value: "booking_confirmed", label: "Booking confirmed" },
      { value: "pickup_scheduled", label: "Pickup appointment scheduled" },
      { value: "docs_prepared", label: "Transport documents prepared" },
      { value: "export_compliance_check", label: "Export compliance check completed" },
    ],
  },
  "Booking & Documentation": {
    icon: FileText,
    color: "text-sky-600",
    items: [
      { value: "si_submitted", label: "SI Submitted" },
      { value: "si_confirmed", label: "SI Confirmed" },
      { value: "docs_received", label: "Documents Received" },
      { value: "docs_sent", label: "Documents Sent" },
      { value: "bl_draft", label: "B/L Draft Received" },
      { value: "bl_released", label: "B/L Released" },
      { value: "telex_released", label: "Telex Released" },
      { value: "awb_issued", label: "AWB Issued" },
    ],
  },
  "Pickup & Origin": {
    icon: Truck,
    color: "text-amber-600",
    items: [
      { value: "truck_dispatched_pickup", label: "Truck dispatched for pickup" },
      { value: "pickup_completed", label: "Pickup completed at shipper" },
      { value: "container_pickup", label: "Container Pickup" },
      { value: "proof_of_pickup", label: "Proof of pickup received" },
      { value: "cargo_received_origin", label: "Cargo received at origin warehouse" },
      { value: "cargo_checked", label: "Cargo checked & counted" },
      { value: "cargo_packed", label: "Cargo labeled / palletized / packed" },
      { value: "cargo_consolidated", label: "Cargo consolidated (LCL/groupage)" },
      { value: "weight_verified", label: "Weight and dimensions verified" },
      { value: "dg_inspection", label: "Dangerous goods inspection completed" },
      { value: "ready_for_export", label: "Cargo ready for export customs" },
    ],
  },
  "Export Customs": {
    icon: ShieldCheck,
    color: "text-purple-600",
    items: [
      { value: "export_dec_created", label: "Export declaration created" },
      { value: "export_dec_submitted", label: "Export declaration submitted" },
      { value: "customs_filed", label: "Customs Filed" },
      { value: "customs_inspection_req", label: "Customs inspection requested" },
      { value: "customs_inspection_done", label: "Customs inspection completed" },
      { value: "export_cleared", label: "Export customs cleared" },
      { value: "export_docs_released", label: "Export documents released (MRN/EX1)" },
      { value: "cargo_released_export", label: "Cargo released for export" },
      { value: "security_filing", label: "Security filing submitted (ENS/AES/ISF)" },
      { value: "export_permit_verified", label: "Export permit verified" },
    ],
  },
  "Inspections": {
    icon: ShieldCheck,
    color: "text-amber-600",
    items: [
      { value: "customs_physical_finished", label: "Customs physical check finished" },
      { value: "cargo_damage_finished", label: "Cargo damage inspection finished" },
      { value: "veterinary_finished", label: "Veterinary inspection finished" },
      { value: "phytosanitary_finished", label: "Phytosanitary inspection finished" },
      { value: "inspection_finished", label: "Inspection finished" },
    ],
  },
  "Port/Airport Origin": {
    icon: Anchor,
    color: "text-blue-600",
    items: [
      { value: "delivered_to_terminal", label: "Cargo delivered to port/airport" },
      { value: "gate_in", label: "Gate-in at terminal" },
      { value: "container_gated_in", label: "Container Gated In" },
      { value: "container_gated_out", label: "Container Gated Out" },
      { value: "terminal_receiving", label: "Terminal receiving completed" },
      { value: "terminal_received", label: "Terminal Received" },
      { value: "terminal_released", label: "Terminal Released" },
      { value: "cargo_accepted_carrier", label: "Cargo accepted by carrier" },
      { value: "loaded_vessel", label: "Loaded on vessel/aircraft/train" },
      { value: "on_vessel", label: "Loaded on Vessel" },
      { value: "container_loaded", label: "Container loaded on vessel" },
      { value: "container_stuffed", label: "Container stuffed" },
      { value: "container_sealed", label: "Container sealed" },
      { value: "departure_confirmed", label: "Departure confirmed" },
      { value: "departed_port", label: "Departed Port" },
    ],
  },
  "International Transit": {
    icon: Globe,
    color: "text-cyan-600",
    items: [
      { value: "departure", label: "Departed origin port/airport" },
      { value: "in_transit", label: "In transit" },
      { value: "transshipment", label: "Transshipment" },
      { value: "transshipment_arrival", label: "Arrived at transshipment port" },
      { value: "transshipment_loaded", label: "Loaded onto connecting vessel" },
      { value: "transshipment_departed", label: "Departed transshipment port" },
      { value: "eta_updated", label: "Estimated arrival updated" },
      { value: "delay_detected", label: "Delay detected" },
      { value: "delay_notice", label: "Delay Notice" },
      { value: "rollover", label: "Rollover" },
      { value: "vessel_change", label: "Vessel Change" },
    ],
  },
  "Destination Arrival": {
    icon: MapPin,
    color: "text-green-600",
    items: [
      { value: "arrival", label: "Arrived at destination port/airport" },
      { value: "arrived_port", label: "Arrived at Port" },
      { value: "arrival_confirmed", label: "Vessel/aircraft arrival confirmed" },
      { value: "discharged", label: "Discharged from Vessel" },
      { value: "cargo_unloaded", label: "Cargo unloaded from vessel/aircraft" },
      { value: "terminal_discharge", label: "Terminal discharge completed" },
      { value: "container_available", label: "Container available for pickup" },
    ],
  },
  "Import Customs": {
    icon: ClipboardCheck,
    color: "text-indigo-600",
    items: [
      { value: "import_dec_prepared", label: "Import declaration prepared" },
      { value: "import_dec_submitted", label: "Import declaration submitted" },
      { value: "duties_calculated", label: "Duties & taxes calculated" },
      { value: "customs_hold", label: "Customs Hold" },
      { value: "import_inspection_req", label: "Customs inspection requested" },
      { value: "import_inspection_done", label: "Customs inspection completed" },
      { value: "inspection_ordered", label: "Inspection Ordered" },
      { value: "inspection_passed", label: "Inspection Passed" },
      { value: "inspection_failed", label: "Inspection Failed" },
      { value: "phyto_cleared", label: "Phytosanitary Cleared" },
      { value: "veterinary_cleared", label: "Veterinary Cleared" },
      { value: "duties_paid", label: "Duties and taxes paid" },
      { value: "import_cleared", label: "Import customs cleared" },
      { value: "customs_cleared", label: "Customs Cleared" },
      { value: "customs_released", label: "Customs Released" },
      { value: "customs_release", label: "Customs release issued" },
      { value: "regulatory_inspection", label: "Regulatory inspection completed (FDA etc.)" },
    ],
  },
  "Destination Handling": {
    icon: Warehouse,
    color: "text-orange-600",
    items: [
      { value: "released_from_terminal", label: "Cargo released from terminal" },
      { value: "gate_out", label: "Gate-out from port/airport" },
      { value: "delivery_order_created", label: "Transport order for delivery created" },
      { value: "received_dest_warehouse", label: "Cargo received at destination warehouse" },
      { value: "deconsolidation", label: "Deconsolidation completed" },
      { value: "cross_docking", label: "Sorting / cross-docking completed" },
      { value: "delivery_route_planned", label: "Delivery route planned" },
      { value: "demurrage_start", label: "Demurrage Started" },
      { value: "detention_start", label: "Detention Started" },
    ],
  },
  "Final Delivery": {
    icon: CheckSquare,
    color: "text-emerald-600",
    items: [
      { value: "truck_dispatched_delivery", label: "Truck dispatched for delivery" },
      { value: "out_for_delivery", label: "Out for Delivery" },
      { value: "arrived_consignee", label: "Arrival at consignee" },
      { value: "delivery_completed", label: "Delivery completed" },
      { value: "delivered", label: "Delivered" },
      { value: "pod_signed", label: "Proof of delivery signed (POD)" },
      { value: "pod_received", label: "POD Received" },
      { value: "delivery_docs_returned", label: "Delivery documents returned" },
      { value: "empty_returned", label: "Empty Container Returned" },
    ],
  },
  "Issues & Events": {
    icon: Circle,
    color: "text-rose-600",
    items: [
      { value: "damage_reported", label: "Damage Reported" },
      { value: "claim_filed", label: "Claim Filed" },
      { value: "other", label: "Other" },
    ],
  },
  "Legacy": {
    icon: Circle,
    color: "text-gray-500",
    items: [
      { value: "checkpoint", label: "Checkpoint" },
    ],
  },
};

// Get display info for a milestone event/milestone type
export const getMilestoneDisplay = (eventType) => {
  for (const [groupName, group] of Object.entries(MILESTONE_TEMPLATES)) {
    const item = group.items.find((i) => i.value === eventType);
    if (item) {
      return { label: item.label, icon: group.icon, color: group.color, group: groupName };
    }
  }
  // Fallback for legacy / unknown values
  if (eventType === "arrival") return { label: "Arrival", icon: Anchor, color: "text-blue-500", group: "Legacy" };
  if (eventType === "departure") return { label: "Departure", icon: Ship, color: "text-orange-500", group: "Legacy" };
  return { label: eventType, icon: Circle, color: "text-gray-500", group: "Other" };
};

// Plain label lookup (used by the Outlook plugin and other UI surfaces that
// only need the human-readable string).
export const getMilestoneLabel = (eventType) => getMilestoneDisplay(eventType).label;

export default MILESTONE_TEMPLATES;
